import { inject, Injectable, isDevMode } from '@angular/core'
import { Actions, ofType, createEffect } from '@ngrx/effects'
import { of } from 'rxjs'
import { catchError, map, switchMap, tap, concatMap, withLatestFrom, mergeMap } from 'rxjs/operators'
import { AuthService } from '../../auth/services/auth.service'
import { Router } from '@angular/router'
import { User } from '../../models/user'

import * as ConfigActions from '../config-store/config.actions'
import { JmeleonActionsPermissionService } from '../../modules/permissions'
import { logoutSucceeded } from './user-profile.actions'
import * as RootStoreState from '../root.state'
import * as UserProfileActions from './user-profile.actions'
import { Store } from '@ngrx/store'
import { DynamicRoutesService, NavSupportService } from '../../modules/app-menu'
import { DtoActions } from '../dto-store/dto.index'
import { AuthEventService } from '../../auth/services/auth-event.service'
import { HttpResult } from '@dot-it/jml-lib'

@Injectable()
export class UserProfileEffects {
  private authService = inject(AuthService)
  private actions$ = inject(Actions)
  public router = inject(Router)
  private japs = inject(JmeleonActionsPermissionService)
  private store$ = inject(Store<RootStoreState.State>)
  private dynamicRouteService = inject(DynamicRoutesService)
  private navSupportService = inject(NavSupportService)
  private authEventService = inject(AuthEventService)

  login$ = createEffect(() =>
    this.actions$.pipe(
      ofType(UserProfileActions.loginRequested),
      withLatestFrom(this.navSupportService.hasDesiredUrl$),
      concatMap(([action, hasDesiredUrl]) =>
        this.authService.login(action.userName, action.password).pipe(
          switchMap((httpResult) => this.handleLoginSuccess(httpResult, hasDesiredUrl)),
          catchError((error) => of(UserProfileActions.loginFailed({ error })))
        )
      )
    )
  )

  private handleLoginSuccess(httpResult: HttpResult<User>, hasDesiredUrl: boolean) {
    if (!httpResult.success) {
      return of(UserProfileActions.loginFailed({ error: httpResult.errMsg }))
    }

    const user: User = httpResult.result
    if (isDevMode()) {
      user.allowedActions = user.allowedActions.sort()
    }
    this.japs.initActionsPermittedForCurrentUser(user.allowedActions)

    if (user.user.enforcePasswdChange) {
      this.router.navigateByUrl('/profile')
      return of(UserProfileActions.loginSucceeded({ user }))
    }

    this.navigateToStartRoute(user, hasDesiredUrl)
    return of(UserProfileActions.loginSucceeded({ user }))
  }

  private navigateToStartRoute(user: User, hasDesiredUrl: boolean): void {
    if (hasDesiredUrl) {
      setTimeout(() => {
        this.navSupportService.navigateToDesiredUrl(this.router)
      }, 1500)
    } else {
      const startRoute = user.defaultStartRoute || '/dashboard'
      this.router.navigateByUrl(startRoute)
    }
  }

  logout$ = createEffect(() =>
    this.actions$.pipe(
      ofType(UserProfileActions.logoutRequested),
      tap((action) => console.log('logout? ', action)),
      switchMap((action) =>
        this.authService.logout(action.sendLogoutRequestToServer).pipe(
          tap((_) => this.authEventService.userIsLoggingOut()),
          tap((_) => this.router.navigate(['/login'])),
          tap((_) => this.dynamicRouteService.removeAllDynamicRoutes()),
          switchMap((_) => [
            logoutSucceeded(),
            // remove all entities from the store
            DtoActions.resetAllDTOs()
          ])
        )
      )
    )
  )

  loginSucceeded$ = createEffect(() =>
    this.actions$.pipe(
      ofType(UserProfileActions.loginSucceeded),
      switchMap((_) => [
        DtoActions.resetAllDTOs(),
        ConfigActions.loadConfigsRequested(),
        ConfigActions.loadGroupConfigsRequested()
      ])
    )
  )

  validateRequested$ = createEffect(() =>
    this.actions$.pipe(
      ofType(UserProfileActions.validateTokenRequested),
      withLatestFrom(this.store$),
      mergeMap(([, storeState]) => {
        if (!storeState.userProfile.user || !storeState.userProfile.user.token) {
          return of(UserProfileActions.validateTokenUnnecessary())
        }
        return this.authService.validateToken().pipe(
          tap((result) => {
            if (!result.tokenIsValid) {
              this.router.navigate(['/login'])
            }
          }),
          map((result) =>
            result.tokenIsValid
              ? UserProfileActions.validateTokenSucceeded({ user: result.loginContext })
              : UserProfileActions.validateTokenFailed()
          )
        )
      })
    )
  )

  prolongTokenRequested$ = createEffect(() =>
    this.actions$.pipe(
      ofType(UserProfileActions.prolongTokenRequested),
      withLatestFrom(this.store$),
      mergeMap(([, storeState]) => {
        if (!storeState.userProfile.user || !storeState.userProfile.user.token) {
          return of(UserProfileActions.prolongTokenNotPossible())
        }
        return this.authService.prolongToken().pipe(
          tap((result) => {
            if (!result.tokenIsValid) {
              this.router.navigate(['/login'])
            }
          }),
          map((result) =>
            result.tokenIsValid
              ? UserProfileActions.prolongTokenSucceeded({ user: result.loginContext })
              : UserProfileActions.prolongTokenFailed()
          )
        )
      })
    )
  )

  changePwSucceeded$ = createEffect(() =>
    this.actions$.pipe(
      ofType(UserProfileActions.changePwSucceeded),
      switchMap((_) => [ConfigActions.loadConfigsRequested(), ConfigActions.loadGroupConfigsRequested()])
    )
  )

  reactivateToken$ = createEffect(() =>
    this.actions$.pipe(
      ofType(UserProfileActions.reactivateTokenRequested),
      mergeMap((action) =>
        this.authService
          .login(action.userName, action.password)
          .pipe(
            map((result) =>
              result.success
                ? UserProfileActions.reactivateTokenSucceeded({ user: result.result })
                : UserProfileActions.reactivateTokenFailed({ error: result.errMsg })
            )
          )
      )
    )
  )
}
